import axios from 'axios';

const API_URL = '/api/superuser';

const superuserApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
superuserApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('superuser_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
superuserApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('superuser_token');
      localStorage.removeItem('superuser');
      window.location.href = '/superuser/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const superuserAuth = {
  login: (email, password) => axios.post('/api/superuser/auth/login', { email, password }),
  changePassword: (currentPassword, newPassword) => 
    superuserApi.post('/auth/change-password', { currentPassword, newPassword }),
};

// Instances endpoints
export const instances = {
  getAll: () => superuserApi.get('/instances'),
  getById: (id) => superuserApi.get(`/instances/${id}`),
  create: (data) => superuserApi.post('/instances', data),
  update: (id, data) => superuserApi.put(`/instances/${id}`, data),
  delete: (id) => superuserApi.delete(`/instances/${id}`),
  executeAction: (id, action) => superuserApi.post(`/instances/${id}/action`, { action }),
  getStatus: (id) => superuserApi.get(`/instances/${id}/status`),
  getLogs: (id) => superuserApi.get(`/instances/${id}/logs`),
  // Instalar e actualizar: sempre a partir de uma release aprovada, com imagem versionada.
  // Os antigos sync/deploy/migrate/version foram removidos do produto — copiavam de uma
  // pasta fixa desactualizada e o "update" era um restart, que nunca recolhe imagem nova.
  install: (id, releaseId) => superuserApi.post(`/instances/${id}/install`, { releaseId }),
  updateRelease: (id, releaseId) => superuserApi.post(`/instances/${id}/update`, { releaseId }),
};

/**
 * MagSend de CADA ginásio.
 *
 * Um projecto e uma chave `mgs_live_` por ginásio, criados pela API de operador no servidor do
 * portal. O browser só vê estado: WhatsApp, créditos de SMS, prefixo da chave e — temporariamente
 * — o QR de emparelhamento. Nenhuma chave passa por aqui.
 */
export const magSend = {
  card: (id) => superuserApi.get(`/magsend/instances/${id}`),
  link: (id) => superuserApi.post(`/magsend/instances/${id}/link`),
  apply: (id) => superuserApi.post(`/magsend/instances/${id}/apply`),
  linkAll: () => superuserApi.post('/magsend/instances/link-all'),
  rotate: (id) => superuserApi.post(`/magsend/instances/${id}/rotate`),
  suspend: (id) => superuserApi.post(`/magsend/instances/${id}/suspend`),
  reactivate: (id) => superuserApi.post(`/magsend/instances/${id}/reactivate`),
  connect: (id) => superuserApi.post(`/magsend/instances/${id}/connect`),
  state: (id) => superuserApi.get(`/magsend/instances/${id}/state`),
};

// Releases endpoints
export const releases = {
  getAll: () => superuserApi.get('/releases'),
  getCurrent: () => superuserApi.get('/releases/current'),
  register: (data) => superuserApi.post('/releases', data),
  approve: (id) => superuserApi.post(`/releases/${id}/approve`),
  promote: (id) => superuserApi.post(`/releases/${id}/promote`),
  markFailed: (id) => superuserApi.post(`/releases/${id}/fail`),
};

// SSH Configurations endpoints
export const sshConfigs = {
  getAll: () => superuserApi.get('/sshconfigurations'),
  getById: (id) => superuserApi.get(`/sshconfigurations/${id}`),
  create: (data) => superuserApi.post('/sshconfigurations', data),
  update: (id, data) => superuserApi.put(`/sshconfigurations/${id}`, data),
  delete: (id) => superuserApi.delete(`/sshconfigurations/${id}`),
  testConnection: (id) => superuserApi.post(`/sshconfigurations/${id}/test`),
};

// Billing endpoints
export const billing = {
  getDashboard: () => superuserApi.get('/tenantbilling/dashboard'),
  // Plans
  getPlans: () => superuserApi.get('/tenantbilling/plans'),
  createPlan: (data) => superuserApi.post('/tenantbilling/plans', data),
  updatePlan: (id, data) => superuserApi.put(`/tenantbilling/plans/${id}`, data),
  deletePlan: (id) => superuserApi.delete(`/tenantbilling/plans/${id}`),
  // Subscriptions
  getSubscriptions: () => superuserApi.get('/tenantbilling/subscriptions'),
  createSubscription: (data) => superuserApi.post('/tenantbilling/subscriptions', data),
  suspendSubscription: (id, reason) => superuserApi.put(`/tenantbilling/subscriptions/${id}/suspend`, { reason }),
  reactivateSubscription: (id, reason) => superuserApi.put(`/tenantbilling/subscriptions/${id}/reactivate`, { reason }),
  // Renovar um ciclo (o pagamento chegou) e corrigir a validade com motivo escrito: as duas
  // coisas ficam no histórico com o antes/depois, e é isso que responde a «eu renovei em Março».
  renewSubscription: (id, reason) => superuserApi.post(`/tenantbilling/subscriptions/${id}/renew`, { reason }),
  adjustSubscription: (id, data) => superuserApi.put(`/tenantbilling/subscriptions/${id}`, data),
  // Linha do tempo da instância (alterações, bloqueios e desbloqueios, com autor e motivo).
  getSubscriptionHistory: (instanceId, limit = 100) =>
    superuserApi.get(`/tenantbilling/instances/${instanceId}/subscription-history`, { params: { limit } }),
  // Invoices
  getInvoices: (params) => superuserApi.get('/tenantbilling/invoices', { params }),
  createInvoice: (data) => superuserApi.post('/tenantbilling/invoices', data),
  generateInvoice: (gymInstanceId) => superuserApi.post(`/tenantbilling/invoices/generate/${gymInstanceId}`),
  updateInvoiceStatus: (id, status) => superuserApi.put(`/tenantbilling/invoices/${id}/status`, { status }),
  // Payments
  getPayments: (params) => superuserApi.get('/tenantbilling/payments', { params }),
  recordPayment: (data) => superuserApi.post('/tenantbilling/payments', data),
  // Contacts
  getContact: (gymInstanceId) => superuserApi.get(`/tenantbilling/contacts/${gymInstanceId}`),
  updateContact: (gymInstanceId, data) => superuserApi.put(`/tenantbilling/contacts/${gymInstanceId}`, data),
  // Instance detail
  getInstanceDetail: (id) => superuserApi.get(`/tenantbilling/instances/${id}/detail`),
  blockInstance: (id, reason) => superuserApi.post(`/tenantbilling/instances/${id}/block`, { reason }),
  unblockInstance: (id) => superuserApi.post(`/tenantbilling/instances/${id}/unblock`),
  // Audit
  getAuditLog: (params) => superuserApi.get('/tenantbilling/audit-log', { params }),
  // Token Packs
  getTokenPacks: () => superuserApi.get('/tenantbilling/token-packs'),
  createTokenPack: (data) => superuserApi.post('/tenantbilling/token-packs', data),
  updateTokenPack: (id, data) => superuserApi.put(`/tenantbilling/token-packs/${id}`, data),
  deleteTokenPack: (id) => superuserApi.delete(`/tenantbilling/token-packs/${id}`),
  // SMS Packs
  getSmsPacks: () => superuserApi.get('/tenantbilling/sms-packs'),
  createSmsPack: (data) => superuserApi.post('/tenantbilling/sms-packs', data),
  updateSmsPack: (id, data) => superuserApi.put(`/tenantbilling/sms-packs/${id}`, data),
  deleteSmsPack: (id) => superuserApi.delete(`/tenantbilling/sms-packs/${id}`),
  // Credits / Balances
  getAllCredits: () => superuserApi.get('/tenantbilling/credits'),
  getInstanceCredits: (gymInstanceId) => superuserApi.get(`/tenantbilling/credits/${gymInstanceId}`),
  purchaseCredits: (data) => superuserApi.post('/tenantbilling/credits/purchase', data),
  getCreditPurchases: (params) => superuserApi.get('/tenantbilling/credit-purchases', { params }),
};

// Bloqueio automático por vencimento: o interruptor, a carência configurável e o modo simular.
// `get` devolve também QUEM SERIA BLOQUEADO HOJE e não age; `run(false)` é sempre simulação e
// `run(true)` só age se o interruptor estiver ligado e a simulação desligada (regra do servidor).
export const autoBlock = {
  get: () => superuserApi.get('/tenantbilling/autoblock'),
  save: (settings) => superuserApi.put('/tenantbilling/autoblock', settings),
  run: (apply = false) => superuserApi.post('/tenantbilling/autoblock/run', null, { params: { apply } }),
};

// Manutenção da instalação onde o portal corre
export const maintenance = {
  // Só lê: o que a base desta instalação é agora
  getSchema: () => superuserApi.get('/maintenance/schema'),
  // Cria o que falta e carimba a versão, se ficar conforme o modelo
  provisionSchema: () => superuserApi.post('/maintenance/provision-schema'),
};

// Cópias de segurança por instância.
// O caminho é o do servidor onde ficam os ficheiros: quem opera tem de os poder ir buscar
// mesmo com o portal em baixo. A reposição exige o nome da instância escrito (confirmation).
export const backups = {
  summary: () => superuserApi.get('/instancebackups'),
  detail: (instanceId) => superuserApi.get(`/instancebackups/${instanceId}`),
  now: (instanceId) => superuserApi.post(`/instancebackups/${instanceId}`),
  saveSettings: (instanceId, settings) => superuserApi.put(`/instancebackups/${instanceId}/settings`, settings),
  restore: (instanceId, backupId, confirmation) =>
    superuserApi.post(`/instancebackups/${instanceId}/restore/${backupId}`, { confirmation }),
};

// Estado observável de uma instalação: a fase e o passo onde está, e há quanto tempo o último
// progresso aconteceu (é daí que sai «parada»). O ecrã faz polling enquanto está a decorrer.
export const provisioning = {
  state: (instanceId) => superuserApi.get(`/instanceprovisioning/${instanceId}`),
  abandon: (instanceId) => superuserApi.post(`/instanceprovisioning/${instanceId}/abandon`),
};

// Versão real de uma instância: a release/imagem registadas, a comparação com o que está a correr
// (OK/DRIFT/MISSING/STOPPED/UNKNOWN com motivo) e a reparação a partir do registo — que exige
// confirmação explícita.
export const versions = {
  state: (instanceId) => superuserApi.get(`/instanceversions/${instanceId}`),
  verify: (instanceId) => superuserApi.post(`/instanceversions/${instanceId}/verify`),
  repair: (instanceId, confirm) => superuserApi.post(`/instanceversions/${instanceId}/repair`, { confirm }),
};

// Domínios e certificados HTTPS por instância (Nginx Proxy Manager).
// A integração só existe com Npm:BaseUrl e Npm:CredentialsFile no servidor; sem isso o resumo
// vem com configured: false e o ecrã diz que é à mão. Nenhum pedido inclui credenciais.
export const domains = {
  summary: () => superuserApi.get('/instancedomains'),
  detail: (instanceId) => superuserApi.get(`/instancedomains/${instanceId}`),
  ensure: (instanceId, domain) => superuserApi.post(`/instancedomains/${instanceId}`, { domain }),
};

// Android Builds endpoints
export const androidBuilds = {
  getAll: (params) => superuserApi.get('/androidbuilds', { params }),
  getById: (id) => superuserApi.get(`/androidbuilds/${id}`),
  create: (data) => superuserApi.post('/androidbuilds', data),
  delete: (id) => superuserApi.delete(`/androidbuilds/${id}`),
  download: (id) => superuserApi.get(`/androidbuilds/${id}/download`, { responseType: 'blob' }),
};

// ---------------------------------------------------------------------------
// Módulos de um tenant, a partir do portal de operador.
//
// Vive fora de /api/superuser: reutiliza o mesmo /api/gymmodules que o admin do
// ginásio usa, para não haver duas implementações da mesma regra (dependências
// entre módulos, presets, auditoria) a divergirem com o tempo.
//
// O tenant vai em X-Tenant-Key, exigido explicitamente pelo backend a quem é
// SuperAdmin — nunca há tenant "por omissão" para o operador, para que uma
// alteração nunca possa cair no ginásio errado por distração.
// ---------------------------------------------------------------------------
const tenantScopedApi = axios.create({
  baseURL: '/api/gymmodules',
  headers: { 'Content-Type': 'application/json' },
});

tenantScopedApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('superuser_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const withTenant = (tenantKey) => ({ headers: { 'X-Tenant-Key': tenantKey } });

export const tenantModules = {
  get: (tenantKey) => tenantScopedApi.get('', withTenant(tenantKey)),
  toggle: (tenantKey, feature, enabled) =>
    tenantScopedApi.put(`/${feature}`, { enabled }, withTenant(tenantKey)),
  applyPreset: (tenantKey, name) =>
    tenantScopedApi.post(`/preset/${name}`, null, withTenant(tenantKey)),
  audit: (tenantKey, take = 50) =>
    tenantScopedApi.get('/audit', { ...withTenant(tenantKey), params: { take } }),
};

// Identidade global e acesso de suporte: os operadores da plataforma e a ÚNICA forma de atuar num ginásio (explícita, com motivo, auditada).
export const platform = {
  operators: () => superuserApi.get('/platform/operators'),
  openSupportAccess: (instanceId, reason) => superuserApi.post('/platform/support-access', { instanceId, reason }),
  supportAudit: (take = 50) => superuserApi.get('/platform/support-access/audit', { params: { take } }),
};

// Pedidos de suporte (lado do PORTAL): contadores por estado, lista de todos os pedidos com o
// ginásio de origem, a conversa, a resposta, a mudança de estado e o fecho. Isto é o REGISTO do
// que o ginásio pediu — a «sessão de suporte» (platform acima) é outra coisa: o operador a entrar
// no ginásio.
export const support = {
  list: (params) => superuserApi.get('/supporttickets', { params }),
  get: (id) => superuserApi.get(`/supporttickets/${id}`),
  reply: (id, body) => superuserApi.post(`/supporttickets/${id}/messages`, { body }),
  setStatus: (id, status) => superuserApi.put(`/supporttickets/${id}/status`, { status }),
  close: (id) => superuserApi.post(`/supporttickets/${id}/close`),
};

export default superuserApi;
